<template>
  <div ref="wrap" class="chevron-wrap" :style="{ height: height + 'px'}">
    <div
      class="chevron-inner"
      :style="{
        transform: `scale(${scale})`,
        transformOrigin: 'left center',
        height: height + 'px',
        gap: gap,
      }"
    >
      <div
        v-for="idx in total"
        :key="idx"
        class="chevron-step"
        :class="{ 'is-first': idx === 1 }"
        :style="idx === 1 ? null : { marginLeft: `-${overlap}px` }"
      >
        <template v-if="idx - 1 === 0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 60 24"
            :height="height"
            :width="stepWidth(idx - 1)"
            fill="none"
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              d="M1 0.5H44.5518C44.6646 0.500016 44.7739 0.538306 44.8623 0.608398L58.7041 11.6084C58.9559 11.8086 58.9559 12.1914 58.7041 12.3916L44.8623 23.3916C44.7739 23.4617 44.6646 23.5 44.5518 23.5H1C0.723858 23.5 0.5 23.2761 0.5 23V1C0.5 0.723858 0.723858 0.5 1 0.5Z"
              :fill="isFilled(idx - 1) ? color : 'none'"
              :stroke="isFilled(idx - 1) ? 'none' : strokeColor"
            />
          </svg>
        </template>

        <template v-else-if="idx - 1 === total - 1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 44 24"
            :height="height"
            :width="stepWidth(idx - 1)"
            fill="none"
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              d="M44 23.4C44 23.7314 43.6608 24 43.2424 24H0.759141C0.0829624 24 -0.254512 23.3517 0.225199 22.9743L13.6335 12.4257C13.9317 12.191 13.9317 11.809 13.6335 11.5743L0.225198 1.02567C-0.254512 0.648264 0.0829638 0 0.759142 0H43.2424C43.6608 0 44 0.268629 44 0.6V23.4Z"
              :fill="isFilled(idx - 1) ? color : 'none'"
              :stroke="isFilled(idx - 1) ? 'none' : strokeColor"
            />
          </svg>
        </template>

        <template v-else>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 59 24"
            :height="height"
            :width="stepWidth(idx - 1)"
            fill="none"
            preserveAspectRatio="xMidYMid meet"
          >
            <path
              d="M58.7761 11.5743C59.0746 11.809 59.0746 12.191 58.7761 12.4257L44.2699 23.8257C44.1278 23.9373 43.9356 24 43.7353 24H0.75997C0.0830529 24 -0.254789 23.3517 0.225444 22.9743L13.6484 12.4257C13.9469 12.191 13.9469 11.809 13.6484 11.5743L0.225444 1.02567C-0.25479 0.648264 0.0830543 0 0.759971 0H43.7353C43.9356 0 44.1278 0.0626702 44.2699 0.174334L58.7761 11.5743Z"
              :fill="isFilled(idx - 1) ? color : 'none'"
              :stroke="isFilled(idx - 1) ? 'none' : strokeColor"
            />
          </svg>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ChevronSteps",
  props: {
    total: { type: Number, required: true },
    filledCount: { type: Number, default: null },
    filledIndexes: { type: Array, default: null },
    color: { type: String, default: "#23BF6A" },
    strokeColor: { type: String, default: "#8E9191" },
    height: { type: Number, default: 24 },
    gap: { type: String, default: "0px" },
    overlap: { type: Number, default: 6 },
  },

  data() {
    return {
      scale: 1,
      ro: null,
      raf: null,
    };
  },

  computed: {
    filledSet() {
      if (this.filledIndexes && this.filledIndexes.length) {
        return new Set(this.filledIndexes);
      }
      const n = Math.max(0, Math.min(this.total, this.filledCount ?? 0));
      return new Set(Array.from({ length: n }, (_, i) => i));
    },

    intrinsicWidth() {
      const t = Number(this.total) || 0;
      if (t <= 0) return 0;
      if (t === 1) return this.firstW;

      const sum =
        this.firstW +
        this.lastW +
        Math.max(0, t - 2) * this.midW;

      const overlaps = (t - 1) * this.overlap;

      return Math.max(0, sum - overlaps);
    },

    firstW() {
      return (60 * this.height) / 24;
    },
    midW() {
      return (59 * this.height) / 24;
    },
    lastW() {
      return (44 * this.height) / 24;
    },

  },

  watch: {
    total: "queueUpdateScale",
    height: "queueUpdateScale",
    overlap: "queueUpdateScale",
    gap: "queueUpdateScale",
  },

  mounted() {
    this.queueUpdateScale();

    const wrap = this.$refs.wrap;
    if (wrap && "ResizeObserver" in window) {
      this.ro = new ResizeObserver(() => this.queueUpdateScale());
      this.ro.observe(wrap);
    } else {
      window.addEventListener("resize", this.queueUpdateScale, { passive: true });
    }
  },

  beforeUnmount() {
    if (this.ro) this.ro.disconnect();
    this.ro = null;

    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;

    window.removeEventListener("resize", this.queueUpdateScale);
  },

  methods: {
    isFilled(idx) {
      return this.filledSet.has(idx);
    },

    stepWidth(idx0) {
      if (idx0 === 0) return this.firstW;
      if (idx0 === this.total - 1) return this.lastW;
      return this.midW;
    },

    queueUpdateScale() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.updateScale());
    },

    updateScale() {
      const wrap = this.$refs.wrap;
      if (!wrap) return;

      const available = wrap.clientWidth || 0;
      const needed = this.intrinsicWidth || 0;

      if (!available || !needed) {
        this.scale = 1;
        return;
      }

      const s = Math.min(1, available / needed);
      this.scale = Number.isFinite(s) ? s : 1;
    },
  },
};
</script>

<style scoped>
.chevron-wrap {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  line-height: 0;
  text-align: left;
}

.chevron-inner {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 auto;
  white-space: nowrap;
}

.chevron-step {
  display: inline-flex;
  align-items: center;
}

.chevron-step svg {
  display: block;
}
</style>
